// Branch Summary display helpers — read-only presentation of figures already returned by the API.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { exportToCsv, slug } from '@/lib/exportCsv';
import { CheckExportBtn } from '@/components/CheckExportBtn';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { IbCounterpartyTable } from '@/components/accounting/BranchCashBankSections';

const n = (v) => Number(v || 0);
const fm = (v) => `${cur()} ${n(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (v) => (n(v) < 0 ? 'text-error' : 'text-foreground');

export const GROUP_HINTS = {
  Salaries: 'Payroll, salary / wage expenses, partner salary, staff benefits, tickets',
  Utilities: 'Electricity, water, internet, phone, gas',
  Rent: 'Shop, flat and staff-accommodation rent and leases',
  Maintenance: 'Repairs, AC, cleaning and servicing',
  'Government Fees': 'Iqama / ID / visa, licenses, municipality, fines, government fees',
  'Bank Charges': 'Bank fees and charges',
  Interbranch: 'Interbranch-labelled expenses (normally settled, not profit)',
  'Other Expenses': 'Every other operating expense. Excludes supplier purchases (cost of sales) and supplier payments',
};

export function useExpenseGroups(branchId, start, end) {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let live = true;
    setState({ data: null, error: null });
    api.get('/accounting/branch-expense-groups', { params: { branch_id: branchId, start_date: start, end_date: end } })
      .then((r) => live && setState({ data: r.data?.groups || {}, error: null }))
      .catch((e) => live && setState({ data: null, error: e }));
    return () => { live = false; };
  }, [branchId, start, end]);
  return state;
}

const EXPORT_COLS = [
  { key: 'source', label: 'Source' },
  { key: 'date', label: 'Date' },
  { key: 'category', label: 'Category' },
  { key: 'description', label: 'Description / Employee' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'invoice_number', label: 'Invoice' },
  { key: 'payment_mode', label: 'Payment Method' },
  { key: 'amount', label: 'Amount', numeric: true, format: (v) => (v === '' ? '' : n(v).toFixed(2)) },
];

function exportSpec(g, detail, b, period) {
  const totals = [
    { source: 'TOTAL', amount: detail.total },
    { source: 'Paid cash', amount: detail.cash },
    { source: 'Paid bank', amount: detail.bank },
    { source: 'Unpaid / credit', amount: detail.credit },
    ...(n(detail.other) ? [{ source: 'Other / unspecified method', amount: detail.other }] : []),
  ].map((r) => ({ date: '', category: '', description: '', supplier: '', invoice_number: '', payment_mode: '', ...r }));
  return { title: `${g} expenses — ${b.branch_name}`, subtitle: `${period.start} to ${period.end} · ${GROUP_HINTS[g] || ''}`,
    filename: `${slug(g)}_${slug(b.branch_name)}_${period.start}_${period.end}`, cols: EXPORT_COLS, rows: [...(detail.rows || []), ...totals] };
}

function CheckDialog({ chk, onClose, b, period, start, end }) {
  if (!chk) return null;
  const { g, d, shown } = chk;
  const rowsSum = Math.round((d.rows || []).reduce((a, r) => a + n(r.amount), 0) * 100) / 100;
  const sameFilters = (period?.start || '') === (start || '') && (period?.end || '') === (end || '');
  const diff = Math.round((n(d.total) - n(shown)) * 100) / 100;
  const ok = sameFilters && Math.abs(diff) < 0.01 && Math.abs(rowsSum - n(d.total)) < 0.01;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="category-check-dialog">
        <DialogHeader><DialogTitle>{g} — {b.branch_name} · {start} to {end}</DialogTitle></DialogHeader>
        <div className={`rounded-md px-3 py-2 text-xs ${ok ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'}`} data-testid="category-check-result">
          {ok ? `Matches: ${d.rows.length} transactions total ${fm(d.total)}, same as the summary figure.`
            : `Does NOT match: summary shows ${fm(shown)}, transactions total ${fm(rowsSum)} (difference ${fm(n(rowsSum) - n(shown))})${sameFilters ? '' : ` · date filter differs (summary ${period?.start} – ${period?.end})`}.`}
          <div className="text-[10px] opacity-80 mt-0.5">Cash {fm(d.cash)} · Bank {fm(d.bank)} · Unpaid/credit {fm(d.credit)}{n(d.other) ? ` · No method ${fm(d.other)}` : ''}</div>
        </div>
        <div className="max-h-80 overflow-auto border border-border rounded-md">
          <table className="w-full text-[11px]">
            <thead className="bg-muted sticky top-0"><tr>{['Date', 'Category', 'Description', 'Supplier', 'Method', 'Amount'].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
            <tbody>
              {(d.rows || []).map((r) => (
                <tr key={`${r.source}-${r.id}`} className="border-t border-border/50">
                  <td className="px-2 py-0.5 whitespace-nowrap">{r.date}</td><td className="px-2 py-0.5">{r.category}</td>
                  <td className="px-2 py-0.5 truncate max-w-[220px]">{r.description}</td><td className="px-2 py-0.5">{r.supplier}</td>
                  <td className="px-2 py-0.5">{r.payment_mode || '—'}</td><td className="px-2 py-0.5 text-right font-mono">{fm(r.amount)}</td>
                </tr>
              ))}
              {!(d.rows || []).length && <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">No {g} transactions in this period</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="category-check-close">Close</Button>
          <ExportTrio spec={() => exportSpec(g, d, b, { start, end })} disabled={!(d.rows || []).length} testId="category-check-export" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SplitLine({ d, testId }) {
  if (!d || !d.count) return null;
  return (
    <div className="flex flex-wrap gap-x-3 text-[10px] text-muted-foreground" data-testid={testId}>
      <span>Cash <b className="font-mono font-medium">{fm(d.cash)}</b></span>
      <span>Bank <b className="font-mono font-medium">{fm(d.bank)}</b></span>
      <span className={n(d.credit) ? 'text-rose-700' : ''}>Unpaid/credit <b className="font-mono font-medium">{fm(d.credit)}</b></span>
      {n(d.other) !== 0 && <span className="text-amber-700">No method <b className="font-mono font-medium">{fm(d.other)}</b></span>}
      <span>· {d.count} txn</span>
    </div>
  );
}

export function ExpenseGroupRows({ b, groups, period, start, end }) {
  const { data, error } = useExpenseGroups(b.branch_id, start, end);
  const [open, setOpen] = useState(null);
  const [chk, setChk] = useState(null);
  const check = (g, d, shown) => (d ? setChk({ g, d, shown }) : toast.error('Category details are still loading — try again in a moment'));
  return <>{groups.map((g) => {
    const d = data?.[g];
    const shown = b.expenses_by_group?.[g] || 0;
    const cats = Object.entries(d?.categories || {});
    return (
      <div key={g} className="px-4 py-1.5 border-t border-border" data-testid={`exp-group-row-${slug(g)}-${b.branch_id}`}>
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => setOpen(open === g ? null : g)} className="flex items-center gap-1 pl-3 text-left min-w-0"
            data-testid={`exp-group-toggle-${slug(g)}-${b.branch_id}`}>
            <ChevronRight size={11} className={`shrink-0 text-muted-foreground transition-transform ${open === g ? 'rotate-90' : ''}`} />
            <span className="text-xs text-foreground">{g}</span>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <CheckExportBtn onClick={() => check(g, d, shown)} testId={`check-export-group-${slug(g)}-${b.branch_id}`}
              title={`Export only ${g} transactions for this branch and period, with payment method and total`} />
            <span className="font-mono text-xs w-28 text-right" data-testid={`exp-group-value-${slug(g)}-${b.branch_id}`}>{fm(shown)}</span>
          </div>
        </div>
        <div className="pl-7 text-[10px] text-muted-foreground leading-snug">{GROUP_HINTS[g] || 'Custom expense group'}</div>
        <div className="pl-7"><SplitLine d={d} testId={`exp-group-split-${slug(g)}-${b.branch_id}`} /></div>
        {error && <div className="pl-7 text-[10px] text-amber-700">Breakdown unavailable</div>}
        {open === g && cats.length > 0 && (
          <div className="pl-7 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4" data-testid={`exp-group-cats-${slug(g)}-${b.branch_id}`}>
            {cats.map(([c, v]) => (
              <div key={c} className="flex justify-between text-[10px]"><span className="truncate text-muted-foreground">{c}</span><span className="font-mono">{fm(v)}</span></div>
            ))}
          </div>
        )}
      </div>
    );
  })}
  <CheckDialog chk={chk} onClose={() => setChk(null)} b={b} period={period} start={start} end={end} />
  </>;
}

// State-based (not <details>): html2canvas "Copy as Image" renders closed <details> content on top of the next headers.
export function Section({ title, hint, children, testId, defaultOpen = false, action, lazy = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [seen, setSeen] = useState(defaultOpen);
  return (
    <div className="border-t border-border" data-testid={testId} data-open={open}>
      <div className="flex items-center bg-muted/40 hover:bg-muted/70 transition-colors">
      <button type="button" onClick={() => { setOpen((o) => !o); setSeen(true); }} aria-expanded={open}
        className="flex-1 min-w-0 text-left cursor-pointer px-3 sm:px-4 py-2 flex flex-wrap items-start sm:items-center justify-between gap-x-3 gap-y-0.5" data-testid={`${testId}-toggle`}>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground min-w-0">
          <ChevronRight size={12} className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />{title}
        </span>
        {hint && <span className="text-[10px] text-muted-foreground text-right ml-auto break-words">{hint}</span>}
      </button>
      {action && <div className="pr-3 shrink-0">{action}</div>}
      </div>
      <div className="bg-background overflow-x-auto" hidden={!open}>{(!lazy || seen) && children}</div>
    </div>
  );
}

function Tile({ label, value, sub, testId, strong }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2 bg-white" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono text-sm ${strong ? 'font-bold' : 'font-semibold'} ${tone(value)}`}>{fm(value)}</div>
      {sub && <div className="text-[10px] text-muted-foreground leading-snug">{sub}</div>}
    </div>
  );
}

export function platformFigures(pr) {
  if (!pr) return null;
  const opening = n(pr.sales_to_date) - n(pr.sales_period) - (n(pr.settled_sales_to_date) - n(pr.settled_sales_period));
  return { opening, sales: n(pr.sales_period), settled: n(pr.settled_sales_period), received: n(pr.received_period), fees: n(pr.commission_period), outstanding: n(pr.outstanding_calculated) };
}

export function BusinessSummary({ b }) {
  const cp = b.cash_position || {};
  const pf = platformFigures(cp.platform_receivable);
  const costs = n(b.cogs) + n(b.expenses_total);
  return (
    <div className="px-4 py-3 border-t border-border bg-slate-50/60" data-testid={`business-summary-${b.branch_id}`}>
      <div className="text-xs font-semibold mb-2">At a glance</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <Tile label="Sales" value={b.sales?.total} sub="All sales recorded this period" testId={`bsum-sales-${b.branch_id}`} />
        <Tile label="Costs" value={costs} sub={`Purchases (COGS) ${fm(b.cogs)} + expenses ${fm(b.expenses_total)}`} testId={`bsum-costs-${b.branch_id}`} />
        <Tile label={n(b.net) >= 0 ? 'Net profit' : 'Net loss'} value={b.net} strong
          sub={n(b.inter_branch_adjustment) ? `Includes inter-branch adjustment ${fm(b.inter_branch_adjustment)}` : 'No inter-branch adjustment'} testId={`bsum-net-${b.branch_id}`} />
        <Tile label="Cash movement (period)" value={cp.cash_in_hand} sub="Cash received − cash paid. Not a verified cash balance" testId={`bsum-cash-${b.branch_id}`} />
        <Tile label="Bank movement (period)" value={cp.bank_in_hand} sub="Bank received − bank paid. Not a verified bank balance" testId={`bsum-bank-${b.branch_id}`} />
        {pf && (pf.sales || pf.outstanding) ? (
          <Tile label="Platform receivable (outstanding)" value={pf.outstanding} sub="Owed by delivery platforms, all periods to date" testId={`bsum-platform-${b.branch_id}`} />
        ) : null}
      </div>
    </div>
  );
}

function L({ label, value, testId, bold, hint }) {
  return (
    <div className={`flex justify-between gap-3 text-xs py-0.5 ${bold ? 'font-semibold' : ''}`} data-testid={testId}>
      <span className="text-muted-foreground">{label}{hint && <span className="block text-[10px] opacity-80">{hint}</span>}</span>
      <span className={`font-mono shrink-0 ${tone(value)}`}>{fm(value)}</span>
    </div>
  );
}

export function PlatformSection({ b }) {
  const pr = b.cash_position?.platform_receivable;
  const pf = platformFigures(pr);
  if (!pf || (!pf.sales && !pf.outstanding && !pf.opening)) return <div className="px-4 py-2 text-xs text-muted-foreground italic">No platform sales or settlements.</div>;
  return (
    <div className="px-4 py-2 space-y-0.5" data-testid={`platform-section-${b.branch_id}`}>
      <L label="Opening outstanding" hint="Unsettled platform sales from earlier periods" value={pf.opening} testId="pf-opening" />
      <L label="+ Platform sales this period" value={pf.sales} testId="pf-sales" />
      <L label="− Sales covered by settlements this period" hint="Recorded settlements, allocated to this branch by sales share" value={pf.settled} testId="pf-settled" />
      <L label="= Outstanding receivable" value={pf.outstanding} bold testId="pf-outstanding" />
      <div className="border-t border-border/60 mt-1 pt-1" />
      <L label="Settlement cash received (period)" hint="Estimated by sales share — not bank-verified" value={pf.received} testId="pf-received" />
      <L label="Platform fees / commission (period)" value={pf.fees} testId="pf-fees" />
      <p className="text-[10px] text-muted-foreground pt-1">Outstanding can be higher than this month's platform sales because it also carries unsettled sales from previous periods.</p>
      {(pr.flags || []).length > 0 && <p className="text-[10px] text-amber-700" data-testid="pf-flags">Needs review: {pr.flags.join(', ').replace(/_/g, ' ').toLowerCase()}</p>}
    </div>
  );
}

export function InterBranchSection({ b, ibAll, loadIbAll, ibLoading, op }) {
  const cp = b.cash_position || {};
  useEffect(() => { loadIbAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const periodNet = n(cp.inter_branch_net_outstanding);
  const allNet = ibAll ? n(ibAll.net_outstanding) : null;
  const rows = [
    ...((cp.inter_branch_counterparties?.receivables) || []).map((r) => ({ ...r, kind: 'Owes this branch' })),
    ...((cp.inter_branch_counterparties?.payables) || []).map((r) => ({ ...r, kind: 'This branch owes' })),
  ];
  return (
    <div className="px-4 py-2 space-y-2" data-testid={`ib-section-${b.branch_id}`}>
      <div>
        <div className="text-[11px] font-semibold mb-0.5">This period</div>
        <L label="This branch paid for other branches" value={cp.paid_for_other_branches} testId="ibs-paid-for" />
        <L label="Other branches paid for this branch" value={-n(cp.paid_by_other_branches)} testId="ibs-paid-by" />
        <L label="Settled — already received back" value={-n(cp.inter_branch_settled_in)} testId="ibs-settled-in" />
        <L label="Settled — already paid back" value={cp.inter_branch_settled_out} testId="ibs-settled-out" />
        <L label="Net outstanding from this period" hint="+ others owe this branch · − this branch owes others" value={periodNet} bold testId="ibs-period-net" />
      </div>
      <IbCounterpartyTable b={b} op={op} ibAll={ibAll} />
      <div>
        <div className="text-[11px] font-semibold mb-0.5">All-time position</div>
        {ibLoading && !ibAll ? <div className="text-[10px] text-muted-foreground">Loading…</div> : ibAll ? (
          <>
            <L label="Others owe this branch" value={ibAll.owed_to_us} testId="ibs-all-owed" />
            <L label="This branch owes others" value={-n(ibAll.we_owe)} testId="ibs-all-owe" />
            <L label="Previous periods (all-time − this period)" value={allNet - periodNet} testId="ibs-prev" />
            <L label="Net inter-branch balance (all time)" value={allNet} bold testId="ibs-all-net" />
          </>
        ) : <div className="text-[10px] text-muted-foreground">All-time position unavailable</div>}
      </div>
      <div className="rounded-md bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900" data-testid="ibs-profit-note">
        Amount used in Net Profit: <b className="font-mono">{fm(b.inter_branch_adjustment)}</b> — the gross cost paid for / by other branches this period. Settlements move cash only and do not change profit, so this differs from the net balance above.
      </div>
      <div className="flex justify-between text-[11px]">
        <Link to="/inter-branch-ledger" className="text-info hover:underline" data-testid={`ibs-ledger-link-${b.branch_id}`}>Open Inter-Branch Ledger →</Link>
        {rows.length > 0 && (
          <button type="button" className="text-info hover:underline" data-testid={`ibs-export-${b.branch_id}`}
            onClick={() => exportToCsv(`inter_branch_${slug(b.branch_name)}.csv`, rows, [
              { key: 'kind', label: 'Direction' }, { key: 'branch_name', label: 'Counterparty' },
              { key: 'gross', label: 'Gross', format: (v) => n(v).toFixed(2) }, { key: 'settled', label: 'Settled', format: (v) => n(v).toFixed(2) },
              { key: 'balance', label: 'Remaining', format: (v) => n(v).toFixed(2) }])}>Export CSV</button>
        )}
      </div>
    </div>
  );
}
